// The office-only side of a quote (Session ASH, Q-10): who owns it, the office's own notes, and the working files
// kept against it. None of this reaches the customer: not the accept page, the quote PDF or any quote email.
//
// Owner and notes save through PATCH /api/quotes/:id/internal, which touches nothing else on the quote (no new
// version, no change to what was sent). Documents are PDF or images, filed to Google Drive under "Quote Documents" >
// the quote reference; removing one takes it off the quote (the file stays in Drive).
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import type { QuoteDocumentItem, QuoteOwnerOption } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Loader } from "../../components/Loader";
import { formatDate } from "../../lib/format";

const NOTES_MAX = 5000;
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

export function QuoteOfficePanel({ quoteId }: { quoteId: string }) {
  const [owners, setOwners] = useState<QuoteOwnerOption[]>([]);
  const [ownerId, setOwnerId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [docs, setDocs] = useState<QuoteDocumentItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [removing, setRemoving] = useState<QuoteDocumentItem | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [internal, ownerList, docList] = await Promise.all([
        api.quotes.internal(quoteId),
        api.quotes.owners(),
        api.quotes.documents(quoteId),
      ]);
      setOwnerId(internal.owner?.id ?? "");
      setNotes(internal.internalNotes ?? "");
      setSavedNotes(internal.internalNotes ?? "");
      // The picker lists ACTIVE office people. An owner since deactivated is still the owner, so they are kept as
      // an option (marked) rather than the select silently showing "No owner" (Session ASH review).
      const current = internal.owner;
      setOwners(
        current && !ownerList.owners.some((o) => o.id === current.id)
          ? [...ownerList.owners, { id: current.id, name: `${current.name} (no longer active)` }]
          : ownerList.owners,
      );
      setDocs(docList.documents);
      setLoaded(true);
    } catch (err) {
      setError(friendlyApiError(err, "The office details for this quote could not be loaded."));
    }
  }, [quoteId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveOwner(next: string) {
    const previous = ownerId;
    setOwnerId(next);
    setError(null);
    setMessage(null);
    try {
      const res = await api.quotes.updateInternal(quoteId, { ownerId: next || null });
      setOwnerId(res.owner?.id ?? "");
      setMessage(res.owner ? `Owner set to ${res.owner.name}.` : "Owner cleared.");
    } catch (err) {
      // Put the saved owner back, so the picker never shows an owner that was not saved.
      setOwnerId(previous);
      setError(friendlyApiError(err, "The owner could not be saved."));
    }
  }

  async function saveNotes() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.quotes.updateInternal(quoteId, { internalNotes: notes.trim() ? notes : null });
      setNotes(res.internalNotes ?? "");
      setSavedNotes(res.internalNotes ?? "");
      setMessage(res.internalNotes ? "Notes saved." : "Notes cleared.");
    } catch (err) {
      setError(friendlyApiError(err, "The notes could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  async function upload() {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Choose a PDF or image to upload.");
      return;
    }
    setUploading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.quotes.uploadDocument(quoteId, file, label.trim() || file.name);
      setDocs((d) => [res.document, ...d]);
      setLabel("");
      if (fileRef.current) fileRef.current.value = "";
      setMessage(`${res.document.label} added.`);
    } catch (err) {
      setError(friendlyApiError(err, "The document could not be uploaded."));
    } finally {
      setUploading(false);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await api.quotes.deleteDocument(quoteId, removing.id);
      setDocs((d) => d.filter((x) => x.id !== removing.id));
      setMessage(`${removing.label} removed.`);
      setRemoving(null);
    } catch (err) {
      setRemoveError(friendlyApiError(err, "The document could not be removed."));
    } finally {
      setRemoveBusy(false);
    }
  }

  return (
    <section aria-label="Office only" className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
      <h2 className="font-heading text-lg tracking-wide text-ele-strong">Office only</h2>
      <p className="mt-0.5 text-xs text-ele-body opacity-70">The customer never sees the owner, these notes or these files.</p>

      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="mt-3 text-sm text-ele-green">
          {message}
        </p>
      )}

      {!loaded ? (
        !error && <Loader shape="inline" className="mt-3" />
      ) : (
        <>
          <label className="mt-4 block text-sm text-ele-body">
            Owner
            <select
              value={ownerId}
              onChange={(e) => void saveOwner(e.target.value)}
              className="mt-1 block w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
            >
              <option value="">No owner</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>

          <label className="mt-4 block text-sm text-ele-body">
            Notes
            <textarea
              value={notes}
              maxLength={NOTES_MAX}
              rows={5}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 block w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
            />
          </label>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-ele-faint">
              {notes.length} / {NOTES_MAX}
            </span>
            <button
              type="button"
              onClick={() => void saveNotes()}
              disabled={saving || notes === savedNotes}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body hover:text-ele-strong disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save notes"}
            </button>
          </div>

          <h3 className="mt-5 font-heading text-base text-ele-strong">Documents</h3>
          {docs.length === 0 ? (
            <p className="mt-1 text-sm text-ele-body opacity-70">No documents yet.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {docs.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 text-sm">
                  <a
                    href={api.quotes.documentDownloadUrl(quoteId, d.id)}
                    className="min-w-0 truncate text-ele-blue underline hover:text-ele-strong"
                    title={d.fileName}
                  >
                    {d.label}
                  </a>
                  <span className="flex flex-none items-center gap-2">
                    <span className="text-xs text-ele-faint">{formatDate(d.uploadedAt)}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setRemoveError(null);
                        setRemoving(d);
                      }}
                      className="text-xs text-ele-danger underline"
                    >
                      Remove
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 space-y-2">
            <input
              type="text"
              value={label}
              maxLength={200}
              placeholder="Label (optional)"
              onChange={(e) => setLabel(e.target.value)}
              aria-label="Document label"
              className="block w-full rounded-ele border border-ele-field-border bg-ele-field px-2 py-1.5 text-sm text-ele-strong"
            />
            <input ref={fileRef} type="file" accept={ACCEPT} aria-label="Document file" className="block w-full text-xs text-ele-body" />
            <button
              type="button"
              onClick={() => void upload()}
              disabled={uploading}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body hover:text-ele-strong disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload document"}
            </button>
            <p className="text-xs text-ele-faint">PDF or image, up to 10MB. Filed in Google Drive under Quote Documents.</p>
          </div>
        </>
      )}

      <ConfirmDeleteModal
        open={removing != null}
        title="Remove this document?"
        message={`${removing?.label ?? ""} will be taken off this quote. The file stays in Google Drive.`}
        confirmLabel="Remove"
        busy={removeBusy}
        error={removeError}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
